import React, { useState, useMemo } from 'react';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import Icon from '@salesforce/design-system-react/components/icon';
import { colors, type, space, radius, elevation, formatMoney, formatDateShort } from '../shared/tokens';
import sampleData from '../shared/sampleData';

import ScheduleStrip from '../shared/components/ScheduleStrip';
import PauseUpgradePanel from '../shared/components/PauseUpgradePanel';
import UpgradeAskBanner from '../shared/components/UpgradeAskBanner';
import LapseCountdownChip from '../shared/components/LapseCountdownChip';
import PaymentMethodCard from '../shared/components/PaymentMethodCard';
import SectionCard from '../shared/components/SectionCard';
import Avatar from '../shared/components/Avatar';

const StatusBadge = ({ status }) => {
  const map = {
    Active:  { color: colors.success, dot: colors.success },
    Paused:  { color: colors.warning, dot: colors.warning },
    Lapsed:  { color: colors.error,   dot: colors.error },
    Closed:  { color: colors.textTertiary, dot: colors.textTertiary },
  };
  const c = map[status] || map.Active;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: space[1],
      padding: `3px 10px`,
      background: `${c.color}14`,
      color: c.color,
      border: `1px solid ${c.color}`,
      borderRadius: radius.pill,
      ...type.small,
      fontWeight: 700,
    }}>
      <span style={{ width: 8, height: 8, background: c.dot, borderRadius: '50%' }} />
      {status}
    </span>
  );
};

const RecurringDonation = () => {
  const rd = sampleData.recurringDonation;
  const contact = sampleData.contacts.eleanor;
  const [status, setStatus] = useState(rd.status);

  const paid = rd.installments.filter(i => i.status === 'Paid');
  const scheduled = rd.installments.filter(i => i.status === 'Scheduled');

  const handleAction = (id) => {
    if (id === 'pause') setStatus(status === 'Active' ? 'Paused' : 'Active');
    else if (id === 'cancel') {
      if (window.confirm('Demo: cancel recurring donation?')) setStatus('Closed');
    } else alert(`Demo: ${id}`);
  };

  const effectiveInstallments = useMemo(
    () => status === 'Paused'
      ? rd.installments.map((i, idx) => idx >= rd.installments.length - 2 ? { ...i, status: 'Paused' } : i)
      : rd.installments,
    [rd.installments, status],
  );

  return (
    <div style={{ minHeight: '100%', background: colors.surfaceInk }}>
      {/* Hero */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        padding: `${space[4]}px ${space[6]}px`,
        boxShadow: elevation.sticky,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: space[4], flexWrap: 'wrap' }}>
          <div style={{
            width: 64, height: 64, borderRadius: radius.lg,
            background: `${colors.success}22`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon category="standard" name="recurring_donation" size="medium" style={{ fill: colors.success }} />
          </div>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div style={{ display: 'flex', gap: space[2], alignItems: 'center', flexWrap: 'wrap' }}>
              <h1 style={{ ...type.h1, color: colors.textPrimary, margin: 0 }}>
                {rd.name}
              </h1>
              <StatusBadge status={status} />
              <LapseCountdownChip nextPaymentDate={rd.nextPaymentDate} status={status} />
            </div>
            <div style={{
              display: 'flex', gap: space[4], flexWrap: 'wrap', marginTop: space[2], ...type.small,
              color: colors.textSecondary,
            }}>
              <span>
                <strong style={{ color: colors.textPrimary, fontSize: 16 }}>{formatMoney(rd.amount)}</strong>
                {' '}/ {rd.period.toLowerCase()}
              </span>
              <span>Since {formatDateShort(rd.startDate)}</span>
              <span>Next: {formatDateShort(rd.nextPaymentDate)}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: space[1] }}>
                <Avatar initials={contact.avatarInitials} color={contact.avatarColor} size="xs" />
                {contact.firstName} {contact.lastName}
              </span>
            </div>
          </div>
        </div>
        <div style={{ marginTop: space[4] }}>
          <PauseUpgradePanel status={status} onAction={handleAction} />
        </div>
      </header>

      <div style={{ padding: space[5] }}>
        {/* Schedule strip — the signature */}
        <SectionCard
          title="Payment schedule — last 12 months"
          subtitle="Hover any dot for installment detail"
        >
          <ScheduleStrip installments={effectiveInstallments} />
        </SectionCard>

        {/* KPI tiles */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: space[3],
          marginBottom: space[4],
        }}>
          <MetricTile label="MRR" value={formatMoney(rd.amount)} unit={`/ ${rd.period.toLowerCase()}`} />
          <MetricTile label="Committed Annual Value" value={formatMoney(rd.committedAnnualValue)} unit="next 12 mo" />
          <MetricTile label="Lifetime from recurring" value={formatMoney(rd.lifetimeFromRecurring)} unit={`${paid.length}+ gifts`} />
          <MetricTile label="On-time streak" value={rd.onTimeStreak} unit="payments" color={colors.success} />
          <MetricTile label="Paid / scheduled" value={`${paid.length} / ${scheduled.length}`} unit="installments" />
          <MetricTile label="Payment method" value={`Visa ••${rd.paymentMethod.last4}`} unit={`exp ${String(rd.paymentMethod.expMonth).padStart(2,'0')}/${String(rd.paymentMethod.expYear).slice(-2)}`} />
        </div>

        {/* Upgrade-ask banner (fires when streak >= 10) */}
        {status === 'Active' && rd.onTimeStreak >= 10 && (
          <div style={{ marginBottom: space[4] }}>
            <UpgradeAskBanner
              currentAmount={rd.amount}
              streak={rd.onTimeStreak}
              onSend={(amt) => alert(`Demo: send upgrade ask for ${formatMoney(amt)}`)}
            />
          </div>
        )}

        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
          <Tabs variant="default" id="recurring-tabs">
            <TabsPanel label="Installments">
              <div style={{ padding: space[5] }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', ...type.label, color: colors.textTertiary }}>
                        <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Date</th>
                        <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Amount</th>
                        <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Status</th>
                        <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Method</th>
                        <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {effectiveInstallments.slice().reverse().map((i, idx) => {
                        const isLate = i.status === 'Paid' && false; // placeholder
                        const statusColor = i.status === 'Paid' ? colors.success
                          : i.status === 'Failed' ? colors.error
                          : i.status === 'Paused' ? colors.warning
                          : colors.textTertiary;
                        return (
                          <tr key={idx} style={{ ...type.small }}>
                            <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                              {formatDateShort(i.date)}
                            </td>
                            <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, fontWeight: 700, color: colors.textPrimary }}>
                              {formatMoney(i.amount)}
                            </td>
                            <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, fontWeight: 700, color: statusColor }}>
                              ● {i.status}
                              {isLate && <span style={{ color: colors.warning, marginLeft: 6, fontWeight: 500 }}>late</span>}
                            </td>
                            <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                              {i.method}
                            </td>
                            <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, color: colors.brand, cursor: 'pointer' }}>
                              {i.status === 'Paid' ? 'Receipt' : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsPanel>

            <TabsPanel label="Allocations">
              <div style={{ padding: space[5] }}>
                <SectionCard
                  title="GAU allocations"
                  subtitle="How each installment is split across funds"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
                    {rd.allocations.map(a => (
                      <div key={a.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: space[1] }}>
                          <span style={{ ...type.body, fontWeight: 600 }}>{a.gauName}</span>
                          <span style={{ ...type.body, fontWeight: 700, color: colors.textPrimary }}>
                            {a.percent}% · {formatMoney(a.amount, { cents: true })}/mo
                          </span>
                        </div>
                        <div style={{ height: 10, background: colors.surfaceInk, borderRadius: 5, overflow: 'hidden' }}>
                          <div style={{
                            width: `${a.percent}%`,
                            height: '100%',
                            background: a.gauId === 'gau-annual' ? colors.brand : colors.timeline.call,
                            transition: 'width 240ms',
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{
                    marginTop: space[4],
                    padding: space[3],
                    background: colors.surfaceInk,
                    borderRadius: radius.sm,
                    ...type.small,
                    color: colors.textSecondary,
                  }}>
                    Over the next 12 months this recurring gift will allocate{' '}
                    <strong>{formatMoney(rd.allocations[0].amount * 12)}</strong> to {rd.allocations[0].gauName} and{' '}
                    <strong>{formatMoney(rd.allocations[1].amount * 12)}</strong> to {rd.allocations[1].gauName}.
                  </div>
                </SectionCard>
              </div>
            </TabsPanel>

            <TabsPanel label="Payment method">
              <div style={{ padding: space[5] }}>
                <SectionCard title="Current payment method">
                  <div style={{ maxWidth: 420 }}>
                    <PaymentMethodCard
                      method={rd.paymentMethod}
                      onChange={() => alert('Demo: open change payment method flow')}
                    />
                  </div>
                  <div style={{ ...type.small, color: colors.textTertiary, marginTop: space[3], lineHeight: 1.5 }}>
                    Cards expiring in the next 3 months show a warning. The donor receives
                    a notification 30 days before expiration asking them to update from the
                    donor portal.
                  </div>
                </SectionCard>
              </div>
            </TabsPanel>

            <TabsPanel label="Details">
              <div style={{ padding: space[5] }}>
                <SectionCard title="Recurring donation details">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: space[4] }}>
                    <DetailField label="Recurring type" value={rd.recurringType} />
                    <DetailField label="Installment frequency" value={`Every ${rd.frequency} ${rd.period.toLowerCase().replace(/ly$/,'')}${rd.frequency > 1 ? 's' : ''}`} />
                    <DetailField label="Day of month" value={rd.dayOfMonth} />
                    <DetailField label="Start date" value={formatDateShort(rd.startDate)} />
                    <DetailField label="End date" value={rd.endDate ? formatDateShort(rd.endDate) : 'Open-ended'} />
                    <DetailField label="Next payment" value={formatDateShort(rd.nextPaymentDate)} />
                    <DetailField label="Campaign" value="Spring Annual Fund 2026" isLink />
                    <DetailField label="NPSP API name" value="npsp__Recurring_Donation__c" mono />
                  </div>
                </SectionCard>
              </div>
            </TabsPanel>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

const MetricTile = ({ label, value, unit, color }) => (
  <div style={{
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    padding: `${space[3]}px ${space[4]}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: space[1],
  }}>
    <div style={{ ...type.label, color: colors.textTertiary }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: space[2] }}>
      <span style={{ ...type.kpi, color: color || colors.textPrimary }}>{value}</span>
      {unit && <span style={{ ...type.kpiUnit, color: colors.textTertiary }}>{unit}</span>}
    </div>
  </div>
);

const DetailField = ({ label, value, isLink, mono }) => (
  <div>
    <div style={{ ...type.label, color: colors.textTertiary }}>{label}</div>
    <div style={{
      ...type.body,
      color: isLink ? colors.brand : colors.textPrimary,
      cursor: isLink ? 'pointer' : 'default',
      fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' : undefined,
      marginTop: 2,
    }}>
      {value}
    </div>
  </div>
);

export default RecurringDonation;
